import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService, JwtResponse } from '../auth.service';
import { UserSessionService } from 'src/app/user-session.service';

@Injectable()
export class JwtRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private redirectInProgress = false;

  private readonly loginRoute = '/connection';
  private readonly authStorageKeys = [
    'tokenConnectedUserWSU1234567GHTVB',
    'tokenConnectedUserOnline',
    'tokenConnectedCaissieronline',
    'tokenConnectedUserGestionaire',
    'tokenConnectedPartneronline',
    'tokenConnectedPartenaireonline',
    'administratorauthorization',
    'PARTNERROLEIST',
    'inTheRoleOf',
    'refreshToken',
    'usernameThatUserIsNowConnect2345789',
    'securityCodeExistForUser',
    'confirmphoneIsOkForThisUser',
    'confirmCNiIstOkForThisUser'
  ];

  constructor(
    private auth: AuthService,
    private router: Router,
    private userSessionService: UserSessionService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Ne pas injecter sur les endpoints d'auth eux-mêmes
    if (req.url.includes('/spay/auth/')) {
      return next.handle(req);
    }

    const token = this.auth.loadToken();
    const authReq = token ? req.clone({ setHeaders: { Authorization: `CBCL ${token}` } }) : req;

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.forceRedirectToLogin();
      return throwError(() => new Error('No refresh token available'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.auth.refresh(refreshToken).pipe(
        switchMap((res: JwtResponse) => {
          this.isRefreshing = false;
          this.auth.saveConnectedUser(res);
          // stocke le nouvel access token et garde le refresh existant
          this.auth.savetoken(res.accessToken || (res as any).token, res.secure, res.confirmphone, res.confirmCNi);
          const nextRefreshToken = res.refreshToken || refreshToken;
          localStorage.setItem('refreshToken', nextRefreshToken);
          this.refreshTokenSubject.next(nextRefreshToken);
          const newToken = this.auth.loadToken();
          return next.handle(request.clone({ setHeaders: { Authorization: `CBCL ${newToken}` } }));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next('');
          this.forceRedirectToLogin();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((token) => {
          if (!token) {
            this.forceRedirectToLogin();
            return throwError(() => new Error('Session expired'));
          }
          const newToken = this.auth.loadToken();
          if (!newToken) {
            this.forceRedirectToLogin();
            return throwError(() => new Error('Session expired'));
          }
          return next.handle(request.clone({ setHeaders: { Authorization: `CBCL ${newToken}` } }));
        })
      );
    }
  }

  private forceRedirectToLogin(): void {
    this.clearAuthSession();

    if (this.redirectInProgress || this.router.url === this.loginRoute) {
      return;
    }

    this.redirectInProgress = true;
    void this.router.navigateByUrl(this.loginRoute).finally(() => {
      this.redirectInProgress = false;
    });
  }

  private clearAuthSession(): void {
    this.authStorageKeys.forEach((key) => localStorage.removeItem(key));
    this.userSessionService.setUser(null);
  }
}
